<script setup lang="ts">
/**
 * The strip right above the composer: follow-up card (chat is due), "someone just replied" prompt, who else is
 * in this chat, and the switch ตอบลูกค้า | โน้ตภายใน (Alt+N). Rendered by ChatView; the composer itself is
 * untouched (Composer.vue) — in note mode ChatView shows NoteComposer instead.
 */
import { computed, onBeforeUnmount, onMounted } from 'vue'
import { collisionText, presenceText } from '../../lib/collab'
import { tabOf } from '../../lib/inbox'
import { useClock } from '../../stores/clock'
import { useCollab } from '../../stores/collab'
import { useInbox } from '../../stores/inbox'
import Icon from '../ui/Icon.vue'
import FollowUpCard from './FollowUpCard.vue'

const props = defineProps<{ convId: string }>()
const collab = useCollab()
const inbox = useInbox()
const clock = useClock()

const conv = computed(() => inbox.convs[props.convId] ?? null)
const isDue = computed(() => !!conv.value && tabOf(conv.value, clock.now) === 'follow_up')
const others = computed(() => collab.others(props.convId))
const typing = computed(() => others.value.filter((o) => o.state === 'typing'))
const line = computed(() => presenceText(others.value))
const lead = computed(() => typing.value[0] ?? others.value[0] ?? null)
const noteMode = computed(() => !!collab.noteMode[props.convId])
const clash = computed(() => (collab.collision?.convId === props.convId ? collab.collision.info : null))
const clashText = computed(() => (clash.value ? collisionText(clash.value, clock.now) : ''))

function setMode(note: boolean): void {
  collab.setNoteMode(props.convId, note)
}

function onKey(e: KeyboardEvent): void {
  if (e.altKey && !e.ctrlKey && !e.metaKey && e.code === 'KeyN') {
    e.preventDefault()
    setMode(!noteMode.value)
  }
}
onMounted(() => window.addEventListener('keydown', onKey))
onBeforeUnmount(() => window.removeEventListener('keydown', onKey))
</script>

<template>
  <div class="cbar">
    <FollowUpCard v-if="isDue && !noteMode" :conv-id="convId" />

    <div v-if="clash && !noteMode" class="clash" role="alertdialog" aria-label="มีคนเพิ่งตอบลูกค้า">
      <Icon name="alertCircle" />
      <span class="clash-t">{{ clashText }}</span>
      <span class="grow"></span>
      <button class="cl-b" @click="collab.dismissCollision()">ยกเลิก</button>
      <button class="cl-b pri" @click="collab.confirmCollision()">ส่งต่อ</button>
    </div>

    <div class="cb-row">
      <div v-if="lead" class="who" role="status" :title="line ?? undefined">
        <span class="pav" :class="['p-' + inbox.staffInfo(lead.staffId).tone, { typing: lead.state === 'typing' }]">{{
          inbox.staffInfo(lead.staffId).initial !== '·' ? inbox.staffInfo(lead.staffId).initial : lead.name.slice(0, 1)
        }}</span>
        <span class="who-t">{{ line }}</span>
        <span v-if="typing.length" class="dots" aria-hidden="true"><i></i><i></i><i></i></span>
      </div>
      <span class="grow"></span>
      <div class="mode" role="group" aria-label="โหมดช่องพิมพ์">
        <button class="md-b" :class="{ on: !noteMode }" :aria-pressed="!noteMode" @click="setMode(false)">
          <Icon name="chat" />ตอบลูกค้า
        </button>
        <button
          class="md-b note"
          :class="{ on: noteMode }"
          :aria-pressed="noteMode"
          title="โน้ตภายใน เห็นเฉพาะพนักงาน (Alt+N)"
          @click="setMode(true)"
        >
          <Icon name="note" />โน้ตภายใน
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.cbar {
  flex: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 16px 0;
  background: #fff;
  border-top: 1px solid var(--line);
}
.cb-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 28px;
  min-width: 0;
}
.who {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font-size: 12.5px;
  line-height: 19px;
  color: #2e3b35;
}
.who-t {
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dots {
  display: inline-flex;
  gap: 3px;
  flex: none;
}
.dots i {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: var(--ac);
  animation: dot 1.2s ease-in-out infinite;
}
.dots i:nth-child(2) {
  animation-delay: 0.15s;
}
.dots i:nth-child(3) {
  animation-delay: 0.3s;
}
@keyframes dot {
  0%,
  60%,
  100% {
    opacity: 0.25;
  }
  30% {
    opacity: 1;
  }
}
@media (prefers-reduced-motion: reduce) {
  .dots i {
    animation: none;
    opacity: 0.6;
  }
}
.mode {
  display: inline-flex;
  padding: 2px;
  gap: 2px;
  background: #eef2f0;
  border-radius: 8px;
  flex: none;
}
.md-b {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 26px;
  padding: 0 10px 0 8px;
  border-radius: 6px;
  font-size: 12.5px;
  font-weight: 500;
  line-height: 19px;
  white-space: nowrap;
  color: #3e4d46;
}
.md-b :deep(.ic) {
  width: 14px;
  height: 14px;
}
.md-b.on {
  background: #fff;
  color: var(--ink);
  font-weight: 600;
  box-shadow:
    0 1px 2px rgba(23, 33, 28, 0.08),
    0 0 0 1px rgba(23, 33, 28, 0.04);
}
.md-b.note.on {
  background: #fff4c7;
  color: #5e4b0c;
}
.clash {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 8px 8px 12px;
  border-radius: 10px;
  background: var(--warns);
  border: 1px solid #f0dca6;
  color: #6b4300;
  font-size: 13px;
  line-height: 20px;
}
.clash > :deep(.ic) {
  width: 16px;
  height: 16px;
  flex: none;
}
.clash-t {
  font-weight: 600;
}
.cl-b {
  height: 30px;
  padding: 0 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  line-height: 20px;
  white-space: nowrap;
  color: #6b4300;
}
.cl-b:hover {
  background: #fbe3ad;
}
.cl-b.pri {
  background: var(--warn);
  color: #fff;
  font-weight: 600;
}
.cl-b.pri:hover {
  background: #6f4100;
}
</style>
